/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {Cart} from "../cart/Cart";
import {Checkout} from "./Checkout";
import {ICheckoutData} from "./CheckoutData";
import {CheckoutRequestHandler, ICheckoutPutParams} from "./requestHandlers/CheckoutRequestHandler";

class CheckoutFactory {
  private existingCheckout: Checkout | null = null;
  private awaitingCheckoutPromise: Promise<ICheckoutData> | null = null;
  private awaitingCheckoutPutPromise: Promise<ICheckoutData> | null = null;

  public clearCheckout() {
    this.existingCheckout = null;
  }

  public async generateCheckout(shopId: number, cart: Cart, config?: ICheckoutPutParams): Promise<Checkout> {
    if (this.existingCheckout && this.existingCheckout.cartData.cartId === cart.cartId) {
      return this.existingCheckout;
    }

    if (this.awaitingCheckoutPromise) {
      await this.awaitingCheckoutPromise;
      if (this.awaitingCheckoutPutPromise) {
        await this.awaitingCheckoutPutPromise;
      }

      return this.existingCheckout!;
    }

    if (this.awaitingCheckoutPutPromise) {
      await this.awaitingCheckoutPutPromise;

      return this.existingCheckout!;
    }

    this.awaitingCheckoutPromise = CheckoutRequestHandler.createCheckout(shopId, cart.cartId);

    const checkoutResponseData = await this.awaitingCheckoutPromise;

    let rawCheckoutData: ICheckoutData;
    if (config) {
      this.awaitingCheckoutPutPromise = CheckoutRequestHandler.putParams(
        shopId,
        checkoutResponseData.checkoutId,
        config,
      );
      rawCheckoutData = await this.awaitingCheckoutPutPromise;
    } else {
      rawCheckoutData = checkoutResponseData;
    }

    const checkout = new Checkout(shopId, rawCheckoutData);
    this.existingCheckout = checkout;
    this.awaitingCheckoutPutPromise = null;
    this.awaitingCheckoutPromise = null;

    cart.emitter.on(() => checkout.get());

    return checkout;
  }
}

const checkoutFactory = new CheckoutFactory();
export {checkoutFactory as CheckoutFactory};
